import { Delete, Info, Star } from "@mui/icons-material";
import { Button, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Tooltip } from "@mui/material";
import Translate from "../utils/Translate";
import { useEffect, useState } from "react";
import { useApi } from "../utils/Api";
import { buildQueryModel } from "../utils/pagination";

export default function DataGridEasyFilters({
  filters,
  currentFilterModel,
  onSelectFilter,
  summaryFilterIds: summaryFilterIdsProp,
  summaryUpdateUrl: summaryUpdateUrlProp,
  refreshTimeStamp,
  actorId,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilterId, setSelectedFilterId] = useState(null);
  const [rowCountSummaries, setRowCountSummaries] = useState([]);
  const [summaryFilterIds] = useState(summaryFilterIdsProp); // Doesn't support changed filters currently
  const [summaryUpdateUrl] = useState(summaryUpdateUrlProp);
  const api = useApi();

  useEffect(() => {
    if (!currentFilterModel || currentFilterModel.items.length === 0) {
      // No current filtering, so no easy filter should be selected
      setSelectedFilterId(null);
    } else if (selectedFilterId) {
      // Here we check if the current filter has changed and we need to clear selected easy filter
      const selectedFilter = filters.find((f) => f.id === selectedFilterId);
      const allFilterItemsMatch = selectedFilter.filterModel.items.every(
        (item, index) => currentFilterModel.items[index].id === item.id,
      );
      if (!allFilterItemsMatch) {
        setSelectedFilterId(null);
      }
    } else {
      // The current filter may match an easy filter, in that case we select it
      const matchingFilter = filters.find((f) =>
        f.filterModel.items.every((item, index) => currentFilterModel.items[index].id === item.id),
      );
      if (matchingFilter) {
        setSelectedFilterId(matchingFilter.id);
      }
    }
  }, [currentFilterModel, filters, selectedFilterId]);

  useEffect(() => {
    if (!summaryFilterIds || summaryFilterIds.length === 0) {
      return;
    }

    const threeMinuteMillis = 3 * 60 * 1000;
    const queryModelEntries = filters
      .filter((x) => summaryFilterIds.includes(x.id))
      .map((x) => [x.id, buildQueryModel(null, x.sortModel, x.filterModel, null)]);
    const request = {
      queryModelsByKey: Object.fromEntries(queryModelEntries),
      actorId: actorId,
    };

    async function updateRowCounts() {
      setIsLoading(true);
      const response = await api.fetchWithOverride(
        summaryUpdateUrl,
        request,
        "POST",
        () => true, // Response always ok, we ignore errors
        null,
        null,
        null,
        false,
        true,
        false,
      );
      setIsLoading(false);
      if (response && response.isSuccessful) {
        const rowCounts = Object.entries(response.rowCountsByKey)
          .map(([key, rowCount]) => {
            const filter = filters.find((f) => f.id == key);
            if (filter) {
              return { id: filter.id, name: filter.name, rowCount };
            } else {
              return null;
            }
          })
          .filter((x) => !!x && x.rowCount > 0);
        setRowCountSummaries(rowCounts);
      }
    }

    updateRowCounts(); // Call once immediately, then later in setInterval
    const intervalId = setInterval(updateRowCounts, threeMinuteMillis);
    return () => clearInterval(intervalId);
  }, [api, summaryFilterIds, summaryUpdateUrl, refreshTimeStamp, filters]);

  async function handleFilterSelected(newFilterId) {
    setSelectedFilterId(newFilterId);
    const selectedFilter = filters.find((f) => f.id === newFilterId);
    setIsLoading(true); // variabeln kommer att sättas false i updateRowCounts eftersom vi vill både ladda data och vänta tills filtrerna uppdaterats
    await onSelectFilter(selectedFilter);
    if (!summaryFilterIds || summaryFilterIds.length === 0) {
      // det finns inga filter och vi behöver inte vänta på updateRowCounts
      return setIsLoading(false);
    }
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1}>
        <Tooltip
          title={
            <div style={{ whiteSpace: "pre-line" }}>
              {filters && filters.length > 0 && filters.map((f) => `${f.name}: ${f.description}`).join("\n\n")}
            </div>
          }
        >
          <IconButton>
            <Info color="primary" />
          </IconButton>
        </Tooltip>

        <FormControl sx={{ minWidth: "200px" }}>
          <InputLabel id="easy-filter">{Translate.get("StdFilter")}</InputLabel>
          <Select
            labelId="easy-filter"
            id="easy-filter"
            value={selectedFilterId ?? ""}
            label={Translate.get("StdFilter")}
            onChange={(event) => handleFilterSelected(event.target.value)}
            disabled={isLoading}
          >
            {filters.map((filter) => (
              <MenuItem key={filter.id} value={filter.id}>
                {filter.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          onClick={() => onSelectFilter(null)}
          variant="outlined"
          disabled={currentFilterModel?.items?.length === 0 || isLoading}
          startIcon={<Delete />}
        >
          {Translate.get("Purge")}
        </Button>

        {/* <Button
        variant="contained"
        onClick={() => setOpenMyFilters(true)}
        startIcon={<Star />}
        style={{ marginLeft: "1em" }}
      >
        {Translate.get("MyFilters")}
      </Button> */}
      </Stack>

      {rowCountSummaries && rowCountSummaries.length > 0 && (
        <Stack direction="row" flexWrap="wrap" columnGap={2} paddingLeft={1}>
          {rowCountSummaries.map((x) => (
            <Button size="small" key={x.name} onClick={() => handleFilterSelected(x.id)} disabled={isLoading}>
              {`${x.name} (${x.rowCount})`}
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
